// Betragszelle der konsolidierten (Verbund-)Ansicht: Summe + Mehrsegment-Ring,
// Klick auf den Ring öffnet die Aufteilung je Firma.
//
// Nicht summierbare Kennzahlen zeigen statt einer Summe die Einzelwerte
// gestapelt (Farbpunkt + Firmenkürzel + Wert) — so gewählt, weil ein
// „6,2 % · 4,8 % · 11,0 %" in einer Spalte nicht erkennen lässt, welcher Wert zu
// welcher Firma gehört.

import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { RdPopover } from "../redesign/table/RdPopover";
import { REFINED } from "../risikoanalyseStep/tokens";
import { aggNumberFormat, formatPartValue } from "./kpiAggregate";
import type { AggCell } from "./kpiAggregate";
import type { FieldType } from "./kpiTypes";
import { KZ_REST } from "./scopeColors";
import { SplitRing } from "./SplitRingSvg";
import { aggregateSegments, buildArcs, largestRemainderPercents, RING_SIZE } from "./splitRing";
import type { RingSegment } from "./splitRing";

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
  },
  amount: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  /**
   * Feste Breite, auch wenn kein Ring gezeichnet wird: ohne den reservierten
   * Platz rückten die Beträge einer Spalte um 28px nach rechts, sobald eine Zeile
   * ringlos ist — die Zahlenkolonne wäre nicht mehr bündig lesbar.
   */
  ringSlot: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: `${RING_SIZE}px`,
    height: `${RING_SIZE}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ringBtn: {
    border: "none",
    background: "none",
    padding: 0,
    borderRadius: "50%",
    cursor: "pointer",
    lineHeight: 0,
    ":focus-visible": {
      outline: `2px solid ${REFINED.aub}`,
      outlineOffset: "2px",
    },
  },
  stack: {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  stackRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: `${REFINED.fs.xs}px`,
  },
  dot: {
    flexGrow: 0,
    flexShrink: 0,
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },
  kuerzel: {
    flexGrow: 0,
    flexShrink: 0,
    fontWeight: 600,
    color: REFINED.ink2,
  },
  stackValue: {
    marginLeft: "auto",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    color: REFINED.ink,
  },
  /**
   * „—" = nicht erfasst. `ink2` statt des zurückgenommenen `ink3`: auf Weiß
   * käme `ink3` nur auf 2,9:1 und wäre damit für die Aussage „hier fehlt ein
   * Wert" schlechter lesbar als jede Zahl daneben.
   */
  empty: {
    flexGrow: 1,
    minWidth: 0,
    textAlign: "right",
    color: REFINED.ink2,
  },
});

/** Breite der Prozentspalte im Popover — konstant, damit die Prozente fluchten. */
const PCT_COL = 38;

/** Eine Zeile im Aufteilungs-Popover. Portal → bewusst ohne Fluent-`tokens.*`. */
const PopRow: React.FC<{
  color: string;
  name: string;
  betrag: string;
  pct: number | null;
  indent?: boolean;
}> = ({ color, name, betrag, pct, indent = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: indent ? "3px 2px 3px 18px" : "4px 2px",
    }}
  >
    <span
      style={{ flex: "0 0 auto", width: 9, height: 9, borderRadius: "50%", background: color }}
    />
    <span
      style={{
        flex: "1 1 auto",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: indent ? REFINED.fs.xs : REFINED.fs.sm,
        color: indent ? REFINED.ink2 : REFINED.ink,
      }}
      title={name}
    >
      {name}
    </span>
    <span
      style={{
        flex: "0 0 auto",
        fontSize: indent ? REFINED.fs.xs : REFINED.fs.sm,
        fontWeight: indent ? 500 : 600,
        color: indent ? REFINED.ink2 : REFINED.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {betrag}
    </span>
    <span
      style={{
        flex: `0 0 ${PCT_COL}px`,
        textAlign: "right",
        fontSize: REFINED.fs.xs,
        color: REFINED.ink2,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {pct === null ? "" : `${pct} %`}
    </span>
  </div>
);

export interface ConsolidatedAmountCellProps {
  cell: AggCell;
  colorOf: (contactId: number) => string;
  /** Bezeichnung der Kennzahl — geht in Popover-Kopf und `aria-label`. */
  label: string;
  year: number;
  /** Editor-Typ der Zeile — bestimmt Zahlformat UND Einheit der Einzelwerte. */
  type: FieldType;
}

export const ConsolidatedAmountCell: React.FC<ConsolidatedAmountCellProps> = ({
  cell,
  colorOf,
  label,
  year,
  type,
}) => {
  const styles = useStyles();
  const formatValue = aggNumberFormat(type);

  // Der Ring-Slot wird in JEDER Variante reserviert — auch ohne Ring. Sonst
  // stünden „—" und gestapelte Einzelwerte um 28px weiter rechts als die
  // Beträge derselben Spalte.
  if (cell.kind === "empty") {
    return (
      <div className={styles.wrap}>
        <span className={styles.empty}>{cell.text}</span>
        <span className={styles.ringSlot} />
      </div>
    );
  }

  if (cell.kind === "list") {
    return (
      <div className={styles.wrap}>
        <div className={styles.stack}>
          {cell.parts.map((p) => (
            <div key={p.contactId} className={styles.stackRow} title={p.name}>
              <span className={styles.dot} style={{ background: colorOf(p.contactId) }} />
              <span className={styles.kuerzel}>{p.kuerzel}</span>
              <span className={styles.stackValue}>{formatPartValue(type, p)}</span>
            </div>
          ))}
        </div>
        <span className={styles.ringSlot} />
      </div>
    );
  }

  // Nur positive, geparste Beiträge haben einen Anteil am Ring. Freitext-Werte
  // ("mixed") und Null-/Minusbeträge bleiben ohne Bogen, tauchen im Popover aber
  // weiter auf — sie sind Teil der Zelle, nur eben nicht Teil der Aufteilung.
  const beitraege = cell.parts.filter((p) => p.num !== null && p.num > 0);
  const ohneAnteil = cell.parts.filter((p) => p.num === null || p.num <= 0);

  const prozente = largestRemainderPercents(beitraege.map((p) => p.num as number));
  const pctOf = new Map(beitraege.map((p, i) => [p.contactId, prozente[i]]));

  /**
   * Bezugsgröße der Prozente = Summe der positiven Beiträge. Sie kann von
   * `cell.total` abweichen (negatives Eigenkapital, unvollständige „≥"-Summe) —
   * dann behauptet die Fußzeile KEINE 100 %, sonst stünde dort „67 %" über einer
   * Gesamtzeile, zu der die 67 % rechnerisch nicht passen.
   */
  const basis = beitraege.reduce((a, p) => a + (p.num as number), 0);
  const basisIstGesamt = cell.total !== null && Math.abs(basis - cell.total) < 0.005;

  const segmente: RingSegment[] = beitraege.map((p) => ({
    contactId: p.contactId,
    name: p.name,
    value: p.num as number,
    color: colorOf(p.contactId),
  }));
  const { shown, rest, restMembers } = aggregateSegments(segmente);
  const segmentliste = rest ? [...shown, rest] : shown;
  const arcs =
    segmentliste.length > 0
      ? buildArcs(
          segmentliste.map((s) => s.value),
          segmentliste.map((s) => s.color),
        )
      : // Kein einziger positiver Beitrag (nur Freitext/Null): ein neutraler
        // Vollkreis als reine Schaltfläche — die Aufteilung muss trotzdem
        // aufrufbar bleiben.
        buildArcs([1], [KZ_REST]);

  /**
   * Aufrufbar, sobald die Zelle aus mehr als einer Firma stammt — NICHT erst ab
   * zwei ringfähigen Beiträgen: gerade die „≥"-Summe (ein Betrag + ein
   * Freitext) sagt dem Nutzer „hier fehlt etwas", und ohne Popover hätte er
   * keinen Weg herauszufinden, WAS fehlt.
   */
  const zeigeRing = cell.parts.length >= 2;

  return (
    <div className={styles.wrap}>
      {/* `title`, weil der Betrag bei sehr langen Zahlen mit Ellipse abschneidet. */}
      <span className={styles.amount} title={cell.text}>
        {cell.text}
      </span>
      <span className={styles.ringSlot}>
        {zeigeRing && (
          <RdPopover
            align="end"
            width={280}
            // Als Dialog: die Aufteilung je Firma steht NUR hier — ohne
            // Fokusverlagerung erreicht sie kein Tastatur-/Screenreader-Nutzer.
            dialogLabel={`${label} ${year} — Aufteilung auf ${cell.parts.length} Firmen`}
            trigger={({ open, toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                className={styles.ringBtn}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={`${label} ${year}: Aufteilung auf ${cell.parts.length} Firmen anzeigen`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
              >
                <SplitRing arcs={arcs} />
              </button>
            )}
          >
            {() => (
              <div className="rd-pop-body">
                <div className="rd-pop-grouptitle">{`${label} · ${year}`}</div>
                <div className="rd-pop-scroll">
                  {shown.map((s) => (
                    <PopRow
                      key={s.contactId}
                      color={s.color}
                      name={s.name}
                      betrag={formatValue(s.value)}
                      pct={pctOf.get(s.contactId) ?? null}
                    />
                  ))}
                  {rest && (
                    <>
                      <PopRow
                        color={rest.color}
                        name={`${rest.name} (${restMembers.length})`}
                        betrag={formatValue(rest.value)}
                        pct={restMembers.reduce((a, m) => a + (pctOf.get(m.contactId) ?? 0), 0)}
                      />
                      {restMembers.map((m) => (
                        <PopRow
                          key={m.contactId}
                          color={m.color}
                          name={m.name}
                          betrag={formatValue(m.value)}
                          pct={pctOf.get(m.contactId) ?? null}
                          indent
                        />
                      ))}
                    </>
                  )}
                  {ohneAnteil.map((p) => (
                    <PopRow
                      key={p.contactId}
                      color={colorOf(p.contactId)}
                      name={p.name}
                      betrag={formatPartValue(type, p)}
                      pct={null}
                    />
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 2,
                    paddingTop: 6,
                    borderTop: `1px solid ${REFINED.line}`,
                  }}
                >
                  <span
                    style={{
                      flex: "1 1 auto",
                      fontSize: REFINED.fs.xxs,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: REFINED.ink2,
                    }}
                  >
                    Gesamt
                  </span>
                  <span
                    style={{
                      flex: "0 0 auto",
                      fontSize: REFINED.fs.sm,
                      fontWeight: 700,
                      color: REFINED.ink,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {cell.text}
                  </span>
                  <span
                    style={{
                      flex: `0 0 ${PCT_COL}px`,
                      textAlign: "right",
                      fontSize: REFINED.fs.xs,
                      fontWeight: 700,
                      color: REFINED.ink2,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {basisIstGesamt ? "100 %" : ""}
                  </span>
                </div>
              </div>
            )}
          </RdPopover>
        )}
      </span>
    </div>
  );
};

export default ConsolidatedAmountCell;
